import React, { useEffect, useMemo, useState } from "react";
import { ControlSpiner } from "../common/Spinner";
import WithSignalR from "../common/withSignalR";
import { EventType } from "../../store/SignalRStore";
import { Dictionary, EntityType, ITimeframe, ServerEntityType, UserPreferencesSettingsUpdate } from "../../entities/common";
import { connect } from "react-redux";
import { distinct, notUndefined, toDateTime } from "../utils/common";
import {
    CreateTimeTrackingEntryAttr,
    ShownInterval,
    TimeTrackingEntry,
    TimeTrackingEntryAttr,
    TimeTrackingLine,
    TimeTrackingTaskDetails,
    actionCreators as TimeTrackingActionCreators,
    TimeTrackingProjectDetails,
    IsEnterpriseTimeTracker,
    TimeTrackingEntrySourceType,
    CreateTimeTrackingEntry,
    Timesheet,
    TimeTrackingSuggestionType,
    CopyBillableAndCostType,
    GetCostType
} from "../../store/TimeTrackingStore";
import { TimeReportingPeriod } from "../../store/TimeReportingPeriodsStore";
import { ApplicationState } from "../../store";
import { getFirstDayOfWeekSetting } from "../../store/selectors/settingsSelectors";
import TimeTrackingMenu from "./TimeTrackingMenu";
import EditTimeTrackingEntry from "./EditTimeTrackingEntry";
import { DayOfWeek, MessageBar, MessageBarType } from "office-ui-fabric-react";
import { getDaysBetweenDates } from "../common/timeline/utils";
import { Resource, ResourceCalendar, calculateResourceCalendar } from "../../store/ResourcesListStore";
import "./timeTrackingGrid.css";
import { TimeTrackingAdministrativeCategory, TimeTrackingGlobalSettings } from "../../store/Tenant";
import { MyWork } from "../../store/MyWorkStore";
import { nameof } from "../../store/services/metadataService";
import TimeTrackingImportTasksPanel from "./import/TimeTrackingImportTasksPanel";
import EditCustomTimeTrackingEntry from "./EditCustomTimeTrackingEntry";
import {
    BuildAssignmentSuggestionTimeTrackingLines,
    BuildEntriesTimeTrackingLines,
    BuildProjectLines,
    BuildResourcePlanSuggestionTimeTrackingLines,
    isProjectReportable,
    ControlSettings,
    MergeTimeTrackingLines,
    GetNewEntryDate,
    useDebounceActions,
    useResourcePlanProjects,
    useSuggestedTasksDetails,
    ShowResourcePlanSmartSuggestion,
    useSuggestedProjectsDetails,
    TimeType,
    IsPeriodOpenForReporting,
    IsTimesheetApprovalsEnabled,
    IsTimesheetReadonly
} from "./common";
import { bindActionCreators } from "redux";
import { isInReadonlyMode } from "../../store/User";
import { addOrUpdateOrRemove } from "../../store/services/storeHelper";
import TimeTrackingImportFromM365 from "./import/TimeTrackingImportFromM365";
import { Office365TimeTrackingConnectorReaders } from "../../store/integration/PersonalOffice365Store";
import TimeTrackingImporExternalSuggestions from "./import/TimeTrackingImporExternalSuggestions";
import { TimeTrackingPersistTimelineList } from "./TimeTrackingPersistTimelineList";
import { CalendarDataSet } from "../../store/calendar/common";

type OwnProps = {
    resource: Resource | undefined;
    resourceCalendar?: ResourceCalendar;
    work: MyWork[];
    controlSettings: ControlSettings;
    onSaveSettings?: (update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => void;
};

type StoreProps = {
    entries: TimeTrackingEntry[];
    isLoading: boolean;
    calendarsById: Dictionary<CalendarDataSet>;
    administrativeCategories: TimeTrackingAdministrativeCategory[];
    settings: TimeTrackingGlobalSettings;
    shownInterval?: ShownInterval;
    tasks: TimeTrackingTaskDetails[];
    projects: TimeTrackingProjectDetails[];
    isReadonlyMode: boolean;
    isEnterpriseTimeTracker: boolean;
    showResourcePlanSmartSuggestion: boolean;
    firstDayOfWeek: DayOfWeek;
    activeResourceId: string | undefined;
    shownPeriod: TimeReportingPeriod | null;
    currentTimesheet: Timesheet | null;
    isTimesheetApprovalsEnabled: boolean;
};

type ActionProps = {
    timeTrackingActions: typeof TimeTrackingActionCreators;
};

type Props = StoreProps & OwnProps & ActionProps;

export interface IMaybeTotal {
    isTotal?: true;
}

export interface WithEntityType {
    entityType?: EntityType;
}

type ModifiedEntry = { entry?: TimeTrackingEntry; timeType?: TimeType; project?: TimeTrackingProjectDetails };

export * from "./common";

const TimeTrackingGrid = (props: Props) => {
    const {
        entries,
        isLoading,
        calendarsById,
        resourceCalendar,
        resource,
        administrativeCategories,
        work,
        timeTrackingActions,
        onSaveSettings,
        controlSettings,
        shownInterval,
        settings,
        isReadonlyMode,
        isEnterpriseTimeTracker,
        showResourcePlanSmartSuggestion,
        activeResourceId,
        shownPeriod,
        currentTimesheet,
        isTimesheetApprovalsEnabled
    } = props;

    const enabledAssignmentSmartSuggestions = settings.enabledAssignmentSmartSuggestions;
    const enablaResourcePlanSmartSuggestion = settings.enablaResourcePlanSmartSuggestion && showResourcePlanSmartSuggestion;

    const isPeriodOpenForReporting = IsPeriodOpenForReporting(shownPeriod);
    const isReadonlyDueToPeriod = !isPeriodOpenForReporting;
    const isTimesheetReadonly = IsTimesheetReadonly(currentTimesheet);

    const [modifiedEntry, setModifiedEntry] = useState<ModifiedEntry>({});

    const { suggestedTasks, loadSuggestedTasks } = useSuggestedTasksDetails();
    const { suggestedProjects, loadSuggestedProjects } = useSuggestedProjectsDetails();

    const tasks = useMemo(() => addOrUpdateOrRemove(props.tasks, suggestedTasks), [props.tasks, suggestedTasks]);
    const projects = useMemo(
        () => addOrUpdateOrRemove(props.projects, suggestedProjects),
        [props.projects, suggestedProjects]
    );

    const { timeFrame } = shownInterval ?? { timeFrame: new Date().getThisWeek(props.firstDayOfWeek) };

    const isSettingsLoaded = !!onSaveSettings;

    const mergeSuggestTimeEntries = useMemo(() => {
        return (controlSettings.suggestTimeEntries ?? true) && isPeriodOpenForReporting && !isTimesheetReadonly;
    }, [controlSettings.suggestTimeEntries, isReadonlyDueToPeriod, isTimesheetReadonly]);

    const resourcePlanProjects = useResourcePlanProjects(
        activeResourceId,
        timeFrame,
        mergeSuggestTimeEntries && enablaResourcePlanSmartSuggestion
    );

    const { debouncedCreate, debouncedUpdate } = useDebounceActions(activeResourceId, timeTrackingActions);

    const resourceCalendarDataSet = useMemo(() => {
        const baseCalendar = resource && calendarsById[resource.calendar.id];
        if (!baseCalendar) {
            return undefined;
        }

        return calculateResourceCalendar(resourceCalendar, baseCalendar);
    }, [resourceCalendar, resource, calendarsById]);

    const { lines, projectLines } = useMemo(() => {
        const entryLines = BuildEntriesTimeTrackingLines(
            entries,
            administrativeCategories,
            timeFrame,
            tasks,
            projects,
            currentTimesheet
        );
        const assignmentSuggestions =
            mergeSuggestTimeEntries && enabledAssignmentSmartSuggestions && work.length
                ? BuildAssignmentSuggestionTimeTrackingLines(work, timeFrame, tasks, projects, currentTimesheet)
                : [];

        const resourcePlanSuggestions =
            mergeSuggestTimeEntries && enablaResourcePlanSmartSuggestion
                ? BuildResourcePlanSuggestionTimeTrackingLines(resourcePlanProjects, currentTimesheet)
                : [];

        const mergedLines = MergeTimeTrackingLines(entryLines, assignmentSuggestions, resourcePlanSuggestions);

        const projectLines = BuildProjectLines(mergedLines);

        return { lines: mergedLines, projectLines };
    }, [
        activeResourceId,
        entries,
        work,
        mergeSuggestTimeEntries,
        administrativeCategories,
        tasks,
        projects,
        resourcePlanProjects,
        currentTimesheet
    ]);

    useEffect(() => {
        if (lines.length && !isLoading && isSettingsLoaded) {
            const suggestedLines = lines.filter(_ => _.attributes.SuggestionType);
            const taskIds = _getTaskIds(suggestedLines).filter(_ => !tasks.some(t => t.id === _));

            loadSuggestedTasks(taskIds);

            const suggestedLineProjectIds = suggestedLines.map(_ => _.attributes.Project.id).filter(distinct);
            const projectIdsToLoad = suggestedLineProjectIds.filter(_ => !projects.some(p => p.id === _));

            loadSuggestedProjects(projectIdsToLoad);
        }
    }, [
        lines
            .filter(_ => _.attributes.SuggestionType)
            .map(_ => _.attributes.Task.id)
            .join(),
        isLoading,
        isSettingsLoaded
    ]);

    useEffect(() => {
        if (!isSettingsLoaded || !activeResourceId) {
            return;
        }

        timeTrackingActions.loadTimeEntries(timeFrame.start, timeFrame.end);
    }, [activeResourceId, timeFrame.start.toDateOnlyString(), timeFrame.end.toDateOnlyString(), isSettingsLoaded]);

    const isGridReady = isSettingsLoaded;

    return (
        <div className="time-tracking-grid">
            <WithSignalR
                events={[
                    {
                        name: EventType.TaskRecalculationFinished,
                        callback: (...taskIds: string[]) => timeTrackingActions.handleTaskRecalculationFinished(taskIds)
                    }
                ]}
            />
            <ControlSpiner isLoading={isLoading} className="show-over">
                {isGridReady && (
                    <>
                        {!shownPeriod && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                Time reporting periods have not been defined yet. Please contact your administrator to create
                                them and enable time tracking.
                            </MessageBar>
                        )}
                        <TimeTrackingMenu
                            isReadonly={isReadonlyMode || isReadonlyDueToPeriod || isTimesheetReadonly || !activeResourceId}
                            onAddTime={type => setModifiedEntry({ timeType: type })}
                            mergeSmartSuggestions={mergeSuggestTimeEntries}
                            setMergeSmartSuggestions={_ =>
                                onSaveSettings?.({
                                    parentSettingsPathKeys: [],
                                    valueBySettingNameMap: {
                                        [nameof<ControlSettings>("suggestTimeEntries")]: _
                                    }
                                })
                            }
                            onFieldsUpdate={() => {
                                timeTrackingActions.loadTimeEntries(timeFrame.start, timeFrame.end);
                            }}
                            showMergeSmartSuggestions={
                                enabledAssignmentSmartSuggestions || enablaResourcePlanSmartSuggestion
                            }
                        />

                        <TimeTrackingPersistTimelineList
                            lines={lines}
                            projectLines={projectLines}
                            timeFrame={timeFrame}
                            isReadonly={isReadonlyMode}
                            isPeriodOpenForReporting={isPeriodOpenForReporting}
                            isLoading={isLoading}
                            resourceCalendarDataSet={resourceCalendarDataSet}
                            controlSettings={controlSettings}
                            firstDayOfWeek={props.firstDayOfWeek}
                            showApprovalColumn={isTimesheetApprovalsEnabled && shownPeriod?.attributes.ApprovalRequired}
                            onEditEntry={(entry, timeType) => {
                                setModifiedEntry({ entry, timeType });
                            }}
                            onAddTime={(project, timeType) => {
                                setModifiedEntry({ project, timeType });
                            }}
                            onCreateEntry={(segment, timeTrackingLine) => {
                                const timeType = timeTrackingLine.attributes.IsAdministrative
                                    ? TimeType.Administrative
                                    : timeTrackingLine.attributes.IsCustomEvent
                                      ? TimeType.CustomEvent
                                      : TimeType.ProjectTask;

                                const newEditedEntry: TimeTrackingEntry = {
                                    id: "",
                                    attributes: {
                                        Date: segment.startDate.toDateOnlyString(),
                                        AdministrativeCategory: timeTrackingLine.attributes.IsAdministrative
                                            ? timeTrackingLine.attributes.Task.id
                                            : null,
                                        CustomTimeEntryName: timeTrackingLine.attributes.IsCustomEvent
                                            ? timeTrackingLine.attributes.Task.name
                                            : null,
                                        Task:
                                            timeTrackingLine.attributes.IsCustomEvent ||
                                            timeTrackingLine.attributes.IsAdministrative
                                                ? undefined
                                                : {
                                                      id: timeTrackingLine.attributes.Task.id,
                                                      name: timeTrackingLine.attributes.Task.name,
                                                      uniqueId: timeTrackingLine.attributes.Task.uniqueId!
                                                  },
                                        Project: timeTrackingLine.attributes.IsAdministrative
                                            ? undefined
                                            : timeTrackingLine.attributes.Project
                                    },
                                    sourceInfos:
                                        timeTrackingLine.attributes.SuggestionType ===
                                        TimeTrackingSuggestionType.ResourcePlan
                                            ? [
                                                  {
                                                      sourceType: "ResourcePlan",
                                                      sourceId: timeTrackingLine.attributes.Project!.id,
                                                      type: TimeTrackingEntrySourceType.ResourcePlaSuggestion
                                                  }
                                              ]
                                            : []
                                };

                                _fillupBillableAndCostTypeFromTimeLine(
                                    newEditedEntry.attributes,
                                    timeTrackingLine,
                                    isEnterpriseTimeTracker,
                                    settings
                                );

                                setModifiedEntry({ entry: newEditedEntry, timeType: timeType });
                            }}
                            onDeleteEntries={ids => {
                                timeTrackingActions.deleteTimeEntries(ids);
                            }}
                            onUpdateEntries={updates => {
                                updates.forEach(update => debouncedUpdate.callAction(update));
                            }}
                            onAddTimeEntries={toAdd => {
                                toAdd.forEach(entry => debouncedCreate.callAction(entry));
                            }}
                            onClearTimeEntries={toClear => {
                                const updates = toClear.map(_ => ({
                                    id: _.id,
                                    attributes: {
                                        [nameof<TimeTrackingEntryAttr>("Duration")]: 0
                                    }
                                }));
                                updates.forEach(update => debouncedUpdate.callAction(update));
                            }}
                            onCopyTask={toCopy => {
                                const toAdd = toCopy.map(_ => ({ attributes: _ }));
                                toAdd.forEach(entry => debouncedCreate.callAction(entry));
                            }}
                            onCloneEntry={toClone => {
                                const toAdd = toClone.map(_ => ({ attributes: _.attributes }));
                                toAdd.forEach(entry => debouncedCreate.callAction(entry));
                            }}
                            onRecallLine={
                                isTimesheetApprovalsEnabled && shownPeriod?.attributes.ApprovalRequired
                                    ? lineId => {
                                          timeTrackingActions.recallTimesheet(shownPeriod!.id, [lineId]);
                                      }
                                    : undefined
                            }
                        />
                    </>
                )}

                {modifiedEntry.entry ? (
                    <EditTimeTrackingPanel
                        modifiedEntry={modifiedEntry}
                        timeFrame={timeFrame}
                        timeTrackingActions={timeTrackingActions}
                        onDismiss={() => setModifiedEntry({})}
                    />
                ) : (
                    <>
                        {modifiedEntry.timeType === TimeType.Administrative && (
                            <EditTimeTrackingPanel
                                modifiedEntry={modifiedEntry}
                                timeFrame={timeFrame}
                                timeTrackingActions={timeTrackingActions}
                                onDismiss={() => setModifiedEntry({})}
                            />
                        )}
                        {modifiedEntry.timeType === TimeType.CustomEvent && (
                            <EditCustomTimeTrackingPanel
                                modifiedEntry={modifiedEntry}
                                timeFrame={timeFrame}
                                timeTrackingActions={timeTrackingActions}
                                onDismiss={() => setModifiedEntry({})}
                            />
                        )}
                        {modifiedEntry.timeType === TimeType.ProjectTask && (
                            <ImportTaskPanel
                                resource={resource!}
                                modifiedEntry={modifiedEntry}
                                timeFrame={timeFrame}
                                timeTrackingActions={timeTrackingActions}
                                entries={entries}
                                lines={lines}
                                mergeSuggestTimeEntries={mergeSuggestTimeEntries}
                                onDismiss={() => setModifiedEntry({})}
                            />
                        )}
                        {modifiedEntry.timeType === TimeType.O365 && (
                            <ImportFromM365Panel
                                modifiedEntry={modifiedEntry}
                                timeFrame={timeFrame}
                                timeTrackingActions={timeTrackingActions}
                                projects={projects}
                                entries={entries}
                                onDismiss={() => setModifiedEntry({})}
                            />
                        )}
                        {modifiedEntry.timeType === TimeType.ExternalSuggestion && (
                            <ImportExternalSuggestionPanel
                                resourceId={activeResourceId!}
                                modifiedEntry={modifiedEntry}
                                timeFrame={timeFrame}
                                timeTrackingActions={timeTrackingActions}
                                projects={projects}
                                entries={entries}
                                onDismiss={() => setModifiedEntry({})}
                            />
                        )}
                    </>
                )}
            </ControlSpiner>
        </div>
    );
};

function EditTimeTrackingPanel(
    props: Readonly<{
        modifiedEntry: ModifiedEntry;
        timeFrame: ITimeframe;
        timeTrackingActions: typeof TimeTrackingActionCreators;
        onDismiss: () => void;
    }>
) {
    const { modifiedEntry, timeFrame, timeTrackingActions, onDismiss } = props;

    return (
        <EditTimeTrackingEntry
            entry={
                modifiedEntry.entry ?? {
                    attributes: {
                        Date: GetNewEntryDate(timeFrame)
                    }
                }
            }
            timeType={modifiedEntry.timeType!}
            onComplete={(id, entityAttributes) => {
                if (id) {
                    timeTrackingActions.updateTimeEntries([{ id: id, attributes: entityAttributes }]);
                } else {
                    const sourceInfo = entityAttributes.CustomTimeEntryName
                        ? modifiedEntry.entry?.sourceInfos?.find(
                              _ => _.type === TimeTrackingEntrySourceType.ResourcePlaSuggestion
                          )
                        : undefined;
                    timeTrackingActions.addTimeEntries([{ attributes: entityAttributes, sourceInfo: sourceInfo }]);
                }

                onDismiss();
            }}
            onDismiss={onDismiss}
        />
    );
}

function EditCustomTimeTrackingPanel(
    props: Readonly<{
        modifiedEntry: ModifiedEntry;
        timeFrame: ITimeframe;
        timeTrackingActions: typeof TimeTrackingActionCreators;
        onDismiss: () => void;
    }>
) {
    const { modifiedEntry, timeFrame, timeTrackingActions, onDismiss } = props;
    return (
        <EditCustomTimeTrackingEntry
            project={modifiedEntry.project}
            timeFrame={timeFrame}
            onComplete={(id, entityAttributes) => {
                if (id) {
                    timeTrackingActions.updateTimeEntries([{ id: id, attributes: entityAttributes }]);
                } else {
                    timeTrackingActions.addTimeEntries([{ attributes: entityAttributes }]);
                }

                onDismiss();
            }}
            onDismiss={onDismiss}
        />
    );
}

function ImportTaskPanel(
    props: Readonly<{
        resource: Resource;
        modifiedEntry: ModifiedEntry;
        timeFrame: ITimeframe;
        timeTrackingActions: typeof TimeTrackingActionCreators;
        entries: TimeTrackingEntry[];
        lines: TimeTrackingLine[];
        mergeSuggestTimeEntries: boolean;
        onDismiss: () => void;
    }>
) {
    const { resource, modifiedEntry, timeFrame, timeTrackingActions, entries, lines, mergeSuggestTimeEntries, onDismiss } =
        props;

    return (
        <TimeTrackingImportTasksPanel
            project={modifiedEntry.project}
            onDismiss={onDismiss}
            onImport={(descriptor, callback, _entity) => {
                const entriesToCreate: CreateTimeTrackingEntryAttr[] = getDaysBetweenDates(
                    timeFrame.start,
                    timeFrame.end
                ).map(date => {
                    return {
                        Date: date.toDateOnlyString(),
                        Duration: 0,
                        Task: {
                            id: descriptor.id
                        },
                        Project: {
                            id: descriptor.parentId!
                        }
                    };
                });

                timeTrackingActions.addTimeEntries(
                    entriesToCreate.map(_ => ({ attributes: _ })),
                    true,
                    callback,
                    callback
                );
            }}
            onUndoImport={(_type, id, callback) => {
                const entriesToRemove = entries.filter(_ => _.attributes.Task?.id === id);

                if (entriesToRemove.length) {
                    timeTrackingActions.deleteTimeEntries(
                        entriesToRemove.map(_ => _.id),
                        callback
                    );
                } else {
                    callback();
                }
            }}
            isEntityImported={(entityExternalId: string) => lines.some(_ => _.attributes.Task?.id === entityExternalId)}
            resource={resource}
            timeFrame={mergeSuggestTimeEntries ? undefined : timeFrame}
            exceptIds={_getTaskIds(lines)}
        />
    );
}

function ImportFromM365Panel(
    props: Readonly<{
        modifiedEntry: ModifiedEntry;
        timeFrame: ITimeframe;
        timeTrackingActions: typeof TimeTrackingActionCreators;
        projects: TimeTrackingProjectDetails[];
        entries: TimeTrackingEntry[];
        onDismiss: () => void;
    }>
) {
    const { modifiedEntry, timeFrame, timeTrackingActions, projects, entries, onDismiss } = props;
    return (
        <TimeTrackingImportFromM365
            timeFrame={timeFrame}
            projects={projects.filter(isProjectReportable)}
            project={modifiedEntry.project}
            onDismiss={onDismiss}
            onImport={(suggestion, callback) => {
                const toCreate: CreateTimeTrackingEntry = {
                    sourceInfo: {
                        type: TimeTrackingEntrySourceType.O365,
                        sourceId: suggestion.id,
                        sourceType: Office365TimeTrackingConnectorReaders[suggestion.reader]
                    },
                    attributes: {
                        Date: suggestion.date,
                        CustomTimeEntryName: suggestion.name,
                        Project: suggestion.project,
                        Duration: suggestion.duration
                    }
                };

                timeTrackingActions.addTimeEntries([toCreate], true, callback, callback);
            }}
            isEntityImported={suggestion => {
                const entry = entries.find(
                    _ =>
                        _.attributes.CustomTimeEntryName === suggestion.name &&
                        _.attributes.Duration &&
                        toDateTime(_.attributes.Date)?.toDateOnlyString() === suggestion.date.toDateOnlyString() &&
                        _.sourceInfos?.some(
                            si => si.type === TimeTrackingEntrySourceType.O365 && si.sourceId === suggestion.id
                        )
                );
                return entry && { duration: entry.attributes.Duration!, project: entry.attributes.Project! };
            }}
        />
    );
}

function ImportExternalSuggestionPanel(
    props: Readonly<{
        resourceId: string;
        modifiedEntry: ModifiedEntry;
        timeFrame: ITimeframe;
        timeTrackingActions: typeof TimeTrackingActionCreators;
        projects: TimeTrackingProjectDetails[];
        entries: TimeTrackingEntry[];
        onDismiss: () => void;
    }>
) {
    const { resourceId, modifiedEntry, timeFrame, timeTrackingActions, projects, entries, onDismiss } = props;
    return (
        <TimeTrackingImporExternalSuggestions
            resourceId={resourceId}
            timeFrame={timeFrame}
            projects={projects.filter(isProjectReportable)}
            project={modifiedEntry.project}
            onDismiss={onDismiss}
            onImport={(suggestion, callback) => {
                const toCreate: CreateTimeTrackingEntry = {
                    sourceInfo: {
                        type: TimeTrackingEntrySourceType.ExternalSuggestion,
                        sourceId: suggestion.id,
                        sourceType: suggestion.entry.sourceInfo?.sourceType
                    },
                    attributes: {
                        ...suggestion.entry.attributes,
                        Duration: suggestion.duration,
                        Project: suggestion.project
                    }
                };

                timeTrackingActions.addTimeEntries([toCreate], true, callback, callback);
            }}
            isEntityImported={suggestion => {
                const entry = entries.find(
                    _ =>
                        _.attributes.Duration &&
                        toDateTime(_.attributes.Date)?.toDateOnlyString() === suggestion.date.toDateOnlyString() &&
                        _.sourceInfos?.some(
                            si => si.type === TimeTrackingEntrySourceType.ExternalSuggestion && si.sourceId === suggestion.id
                        )
                );
                return entry && { duration: entry.attributes.Duration!, project: entry.attributes.Project! };
            }}
        />
    );
}

const _fillupBillableAndCostTypeFromTimeLine = (
    entryAttributes: CreateTimeTrackingEntryAttr,
    timeTrackingLine: TimeTrackingLine,
    isEnterpriseTimeTracker: boolean,
    settings: TimeTrackingGlobalSettings
): void => {
    _fillupBillableAndCostType(
        entryAttributes,
        timeTrackingLine.attributes.ProjectDetails,
        timeTrackingLine.attributes.TaskDetails,
        isEnterpriseTimeTracker,
        settings
    );
};

const _fillupBillableAndCostType = (
    entryAttributes: CreateTimeTrackingEntryAttr,
    projectDetails: TimeTrackingProjectDetails | undefined,
    taskDetails: TimeTrackingTaskDetails | undefined,
    isEnterpriseTimeTracker: boolean,
    settings: TimeTrackingGlobalSettings
): void => {
    if (entryAttributes.Project) {
        const billableEntity =
            isEnterpriseTimeTracker && settings.billableSource === ServerEntityType.Project ? projectDetails : taskDetails;

        CopyBillableAndCostType(entryAttributes, billableEntity, isEnterpriseTimeTracker);

        if (isEnterpriseTimeTracker && settings.billableSource !== settings.costTypeSource) {
            const costTypeEntity = settings.costTypeSource === ServerEntityType.Project ? projectDetails : taskDetails;

            entryAttributes.CostType = GetCostType(costTypeEntity);
        }

        if (entryAttributes.Task && taskDetails) {
            entryAttributes.Task.uniqueId ??= taskDetails.uniqueId;
        }
    }
};

const _getTaskIds = (lines: TimeTrackingLine[]): string[] => {
    const taskIds = lines
        .map(_ => (_.attributes.IsAdministrative || _.attributes.IsCustomEvent ? undefined : _.attributes.Task.id))
        .filter(notUndefined)
        .filter(distinct);

    return taskIds;
};

function mapStateToProps(state: ApplicationState, _ownProps: OwnProps): StoreProps {
    return {
        isLoading: state.timeTracking.isLoading,
        calendarsById: state.calendars.byId,

        entries: state.timeTracking.entries,
        tasks: state.timeTracking.tasks,
        projects: state.timeTracking.projects,

        administrativeCategories: state.tenant.timeTracking.administrativeCategories,
        settings: state.tenant.timeTracking.globalSettings,
        shownInterval: state.timeTracking.shownInterval,
        isReadonlyMode: isInReadonlyMode(state.user, state.tenant),
        isEnterpriseTimeTracker: IsEnterpriseTimeTracker(state.tenant.subscription),
        showResourcePlanSmartSuggestion: ShowResourcePlanSmartSuggestion(state.tenant),
        firstDayOfWeek: getFirstDayOfWeekSetting(state),
        activeResourceId: state.timeTracking.activeResourceId,
        shownPeriod: state.timeTracking.shownPeriod,
        currentTimesheet: state.timeTracking.timesheet,
        isTimesheetApprovalsEnabled: IsTimesheetApprovalsEnabled(state.tenant)
    };
}

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        timeTrackingActions: bindActionCreators(TimeTrackingActionCreators, dispatch)
    };
}

export default connect(mapStateToProps, mergeActionCreators)(TimeTrackingGrid);
